/**
 * Document expiry rules and computation utilities.
 * Used to alert users when client documents need updating.
 */

const SPANISH_MONTHS = {
  enero: 0, febrero: 1, marzo: 2, abril: 3, mayo: 4, junio: 5,
  julio: 6, agosto: 7, septiembre: 8, octubre: 9, noviembre: 10, diciembre: 11,
};

/**
 * Configuration: which documents expire and how.
 * - dateField: the key in the processed data to read
 * - type: 'expiration' (direct date), 'age' (max age from document date), 'fiscalYear'
 * - maxAgeMonths: for 'age' type, how many months before it's expired
 * - warningDays: days before expiry to show a warning
 */
const EXPIRY_RULES = {
  identificacionOficial: {
    dateField: 'ExpirationDate',
    type: 'expiration',
    warningDays: 30,
  },
  comprobanteDomicilio: {
    dateField: 'DocumentDate',
    type: 'age',
    maxAgeMonths: 3,
    warningAgeMonths: 2,
  },
  constanciaSituacionFiscal: {
    dateField: 'RegistrationDate',
    type: 'age',
    maxAgeMonths: 12,
    warningAgeMonths: 11,
  },
  acuseReciboDeclaracion: {
    dateField: 'FiscalYear',
    type: 'fiscalYear',
  },
  declaracionEjercicio: {
    dateField: 'FiscalYear',
    type: 'fiscalYear',
  },
  estadosFinancieros: {
    dateField: 'fiscalYear',
    type: 'fiscalYear',
  },
  estadosFinancierosParciales: {
    dateField: 'periodoFin',
    type: 'age',
    maxAgeMonths: 3,
    warningAgeMonths: 2,
  },
};

/**
 * Safely extract a value from processed data, handling the {value, sources} wrapper.
 */
function extractFieldValue(data, fieldKey) {
  if (!data || !fieldKey) return null;
  const raw = data[fieldKey];
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'object' && !Array.isArray(raw) && raw.hasOwnProperty('value')) {
    return raw.value;
  }
  return raw;
}

/**
 * Parse a date from AI-extracted text. Handles:
 * - ISO: "2025-06-15", "2025-06-15T00:00:00Z"
 * - Mexican: "15/06/2025", "15-06-2025" (DD/MM/YYYY)
 * - Spanish text: "15 de junio de 2025", "junio 2025"
 * - US fallback: "06/15/2025" (only if day > 12)
 * - Year only: "2025", 2025
 * Returns a Date object or null.
 */
function parseDocumentDate(rawValue) {
  if (rawValue === null || rawValue === undefined || rawValue === '') return null;

  // Numeric year
  if (typeof rawValue === 'number') {
    if (rawValue > 1900 && rawValue < 2200) {
      return new Date(rawValue, 11, 31); // End of that fiscal year
    }
    return null;
  }

  const str = String(rawValue).trim();
  if (!str) return null;

  // Year-only string: "2025"
  if (/^\d{4}$/.test(str)) {
    return new Date(parseInt(str, 10), 11, 31);
  }

  // Fiscal year range: "2024-2025" — use the later year
  const rangeMatch = str.match(/(\d{4})\s*[-–]\s*(\d{4})/);
  if (rangeMatch) {
    const year = Math.max(parseInt(rangeMatch[1], 10), parseInt(rangeMatch[2], 10));
    return new Date(year, 11, 31);
  }

  // ISO format: "2025-06-15" or "2025-06-15T..."
  const isoMatch = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoMatch) {
    const d = new Date(parseInt(isoMatch[1], 10), parseInt(isoMatch[2], 10) - 1, parseInt(isoMatch[3], 10));
    if (!isNaN(d.getTime())) return d;
  }

  // Slash or dash separated: "15/06/2025" or "15-06-2025"
  const slashMatch = str.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (slashMatch) {
    let day = parseInt(slashMatch[1], 10);
    let month = parseInt(slashMatch[2], 10);
    const year = parseInt(slashMatch[3], 10);

    // If first number > 12, it must be the day (DD/MM/YYYY)
    // If second number > 12, first must be month (MM/DD/YYYY)
    // Default: DD/MM/YYYY (Mexican convention)
    if (day > 12 && month <= 12) {
      // Already DD/MM/YYYY
    } else if (month > 12 && day <= 12) {
      // Swap: it's MM/DD/YYYY
      [day, month] = [month, day];
    }
    // else ambiguous — assume DD/MM/YYYY

    const d = new Date(year, month - 1, day);
    if (!isNaN(d.getTime())) return d;
  }

  // Spanish text: "15 de junio de 2025" or "junio 2025" or "junio de 2025"
  const spanishFull = str.toLowerCase().match(/(\d{1,2})\s+de\s+(\w+)\s+de\s+(\d{4})/);
  if (spanishFull) {
    const day = parseInt(spanishFull[1], 10);
    const monthIdx = SPANISH_MONTHS[spanishFull[2]];
    const year = parseInt(spanishFull[3], 10);
    if (monthIdx !== undefined) {
      const d = new Date(year, monthIdx, day);
      if (!isNaN(d.getTime())) return d;
    }
  }

  const spanishShort = str.toLowerCase().match(/(\w+)\s+(?:de\s+)?(\d{4})/);
  if (spanishShort) {
    const monthIdx = SPANISH_MONTHS[spanishShort[1]];
    const year = parseInt(spanishShort[2], 10);
    if (monthIdx !== undefined) {
      // Use last day of the month
      const d = new Date(year, monthIdx + 1, 0);
      if (!isNaN(d.getTime())) return d;
    }
  }

  // Last resort: try native Date parsing
  const fallback = new Date(str);
  if (!isNaN(fallback.getTime()) && fallback.getFullYear() > 1900) {
    return fallback;
  }

  return null;
}

/**
 * Compute expiry status for a given document type and its processed data.
 * Returns { status, message, daysRemaining, expiryDate } or null if no rule applies.
 * status: 'valid' | 'warning' | 'expired' | 'unknown'
 */
function computeDocumentExpiryStatus(fileId, data) {
  const rule = EXPIRY_RULES[fileId];
  if (!rule) return null;
  if (!data) return null;

  const rawValue = extractFieldValue(data, rule.dateField);
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return { status: 'unknown', message: null, daysRemaining: null, expiryDate: null };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (rule.type === 'fiscalYear') {
    return computeFiscalYearStatus(rawValue, today);
  }

  const parsedDate = parseDocumentDate(rawValue);
  if (!parsedDate) {
    return { status: 'unknown', message: null, daysRemaining: null, expiryDate: null };
  }

  if (rule.type === 'expiration') {
    return computeExpirationStatus(parsedDate, rule.warningDays, today);
  }

  if (rule.type === 'age') {
    return computeAgeStatus(parsedDate, rule.maxAgeMonths, rule.warningAgeMonths, today);
  }

  return null;
}

function computeExpirationStatus(expiryDate, warningDays, today) {
  const diffMs = expiryDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return {
      status: 'expired',
      message: `Vencido hace ${Math.abs(diffDays)} día${Math.abs(diffDays) !== 1 ? 's' : ''}`,
      daysRemaining: diffDays,
      expiryDate,
    };
  }

  if (diffDays <= warningDays) {
    return {
      status: 'warning',
      message: `Vence en ${diffDays} día${diffDays !== 1 ? 's' : ''}`,
      daysRemaining: diffDays,
      expiryDate,
    };
  }

  return { status: 'valid', message: null, daysRemaining: diffDays, expiryDate };
}

function computeAgeStatus(documentDate, maxAgeMonths, warningAgeMonths, today) {
  const expiryDate = new Date(documentDate);
  expiryDate.setMonth(expiryDate.getMonth() + maxAgeMonths);

  const warningDate = new Date(documentDate);
  warningDate.setMonth(warningDate.getMonth() + warningAgeMonths);

  const diffMs = expiryDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (today >= expiryDate) {
    return {
      status: 'expired',
      message: `Documento con más de ${maxAgeMonths} meses de antigüedad`,
      daysRemaining: diffDays,
      expiryDate,
    };
  }

  if (today >= warningDate) {
    return {
      status: 'warning',
      message: `Vigencia por vencer en ${diffDays} día${diffDays !== 1 ? 's' : ''}`,
      daysRemaining: diffDays,
      expiryDate,
    };
  }

  return { status: 'valid', message: null, daysRemaining: diffDays, expiryDate };
}

function computeFiscalYearStatus(rawValue, today) {
  let fiscalYear;

  if (typeof rawValue === 'number') {
    fiscalYear = rawValue;
  } else {
    const str = String(rawValue).trim();
    // Handle range: "2024-2025"
    const rangeMatch = str.match(/(\d{4})\s*[-–]\s*(\d{4})/);
    if (rangeMatch) {
      fiscalYear = Math.max(parseInt(rangeMatch[1], 10), parseInt(rangeMatch[2], 10));
    } else {
      const yearMatch = str.match(/(\d{4})/);
      if (yearMatch) {
        fiscalYear = parseInt(yearMatch[1], 10);
      }
    }
  }

  if (!fiscalYear) {
    return { status: 'unknown', message: null, daysRemaining: null, expiryDate: null };
  }

  // The most recently completed fiscal year
  const currentYear = today.getFullYear();
  const expectedYear = currentYear - 1;

  if (fiscalYear < expectedYear) {
    //const yearsOld = expectedYear - fiscalYear; // eslint-disable-line no-unused-vars 
    return {
      status: 'expired',
      message: `Ejercicio fiscal desactualizado (${fiscalYear})`,
      daysRemaining: null,
      expiryDate: null,
    };
  }

  return { status: 'valid', message: null, daysRemaining: null, expiryDate: null };
}

/**
 * Aggregate all documents needing attention from processedByFile.
 * Returns an array of { fileId, label, status, message }.
 */
function getDocumentsNeedingAttention(processedByFile, requiredFiles) {
  const results = [];

  for (const [fileId, versions] of Object.entries(processedByFile)) {
    if (!versions || versions.length === 0) continue;

    // Use the most recent version
    const sorted = [...versions].sort((a, b) => b.createdAt - a.createdAt);
    const mostRecent = sorted[0];
    if (!mostRecent?.data) continue;

    const expiryStatus = computeDocumentExpiryStatus(fileId, mostRecent.data);
    if (!expiryStatus) continue;
    if (expiryStatus.status === 'valid' || expiryStatus.status === 'unknown') continue;

    const label = requiredFiles.find(f => f.id === fileId)?.label || fileId;
    results.push({
      fileId,
      label,
      status: expiryStatus.status,
      message: expiryStatus.message,
    });
  }

  // Expired first, then warnings
  results.sort((a, b) => {
    if (a.status === 'expired' && b.status !== 'expired') return -1;
    if (a.status !== 'expired' && b.status === 'expired') return 1;
    return 0;
  });

  return results;
}

/**
 * Compute aggregate expiry status for a client from their links data.
 * Used in the Clientes table to show a quick indicator.
 *
 * @param {string[]} clientLinkIds - array of link IDs from the client document
 * @param {Object} linksById - the linksById map from DataContext
 * @returns {{ expiredCount: number, warningCount: number, worst: 'expired'|'warning'|'valid' }}
 */
function getClientExpiryStatus(clientLinkIds, linksById) {
  const result = { expiredCount: 0, warningCount: 0, worst: 'valid' };
  if (!clientLinkIds || !linksById) return result;

  // Collect processed file results across all links, keyed by fileType
  // Keep only the most recent per fileType
  const mostRecentByType = {};

  for (const linkId of clientLinkIds) {
    const link = linksById[linkId];
    if (!link?.filesData) continue;

    for (const [fileDocId, fileDoc] of Object.entries(link.filesData)) {
      if (!fileDoc?.results || fileDoc.status !== 'procesado') continue;

      // fileDocId format: "fileType_timestamp"
      const underscoreIdx = fileDocId.indexOf('_');
      if (underscoreIdx === -1) continue;
      const fileType = fileDocId.substring(0, underscoreIdx);
      const timestamp = fileDocId.substring(underscoreIdx + 1);

      const existing = mostRecentByType[fileType];
      if (!existing || timestamp > existing.timestamp) {
        mostRecentByType[fileType] = { timestamp, results: fileDoc.results };
      }
    }
  }

  // Check expiry for each document type
  for (const [fileType, { results }] of Object.entries(mostRecentByType)) {
    const status = computeDocumentExpiryStatus(fileType, results);
    if (!status) continue;

    if (status.status === 'expired') {
      result.expiredCount++;
      result.worst = 'expired';
    } else if (status.status === 'warning') {
      result.warningCount++;
      if (result.worst !== 'expired') result.worst = 'warning';
    }
  }

  return result;
}

export {
  EXPIRY_RULES,
  parseDocumentDate,
  extractFieldValue,
  computeDocumentExpiryStatus,
  getDocumentsNeedingAttention,
  getClientExpiryStatus,
};
